import { useEffect } from 'react';
import useDynamicLogo from '../../hooks/useDynamicLogo';
import { DEFAULT_LOGO_URL } from '../../utils/imageUrlHelper';

const getMimeType = (url) => {
  if (!url) {
    return 'image/svg+xml';
  }

  const lowerUrl = url.toLowerCase();
  if (lowerUrl.endsWith('.svg')) {
    return 'image/svg+xml';
  }
  if (lowerUrl.endsWith('.ico')) {
    return 'image/x-icon';
  }

  return 'image/png';
};

const normalizeFaviconFromImage = (src) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.decoding = 'async';
    img.onload = () => {
      try {
        const size = 64;
        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Canvas context unavailable'));
          return;
        }

        // Favicon cuadrado sin deformar: contain + padding fijo.
        const safeZone = 0.82;
        const targetSize = size * safeZone;
        const ratio = Math.min(targetSize / img.width, targetSize / img.height);
        const drawWidth = img.width * ratio;
        const drawHeight = img.height * ratio;
        const x = (size - drawWidth) / 2;
        const y = (size - drawHeight) / 2;

        ctx.clearRect(0, 0, size, size);
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, x, y, drawWidth, drawHeight);

        resolve(canvas.toDataURL('image/png'));
      } catch (error) {
        reject(error);
      }
    };
    img.onerror = () => reject(new Error('Failed loading favicon source image'));
    img.src = src;
  });
};

const setFaviconLinks = (href, mime) => {
  const links = document.querySelectorAll("link[rel*='icon']");

  if (links.length === 0) {
    const link = document.createElement('link');
    link.rel = 'icon';
    link.type = mime;
    link.href = href;
    document.head.appendChild(link);
    return;
  }

  links.forEach((link) => {
    link.type = mime;
    link.href = href;
  });
};

const DynamicFavicon = () => {
  const { logoUrl } = useDynamicLogo();

  useEffect(() => {
    const href = logoUrl || DEFAULT_LOGO_URL;
    const mime = getMimeType(href);
    let isCancelled = false;

    const applyFavicon = async () => {
      try {
        // Siempre intentamos normalizar para evitar distorsión por proporciones extrañas.
        const normalizedHref = await normalizeFaviconFromImage(href);
        if (!isCancelled) {
          setFaviconLinks(normalizedHref, 'image/png');
        }
      } catch (_) {
        // Fallback robusto: usar logo original si canvas falla por CORS/formato.
        if (!isCancelled) {
          setFaviconLinks(href, mime);
        }
      }
    };

    applyFavicon();

    return () => {
      isCancelled = true;
    };
  }, [logoUrl]);

  return null;
};

export default DynamicFavicon;

